"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Loader2, ArrowLeft, ArrowRight, FileText, Scale, TrendingUp, Users, BarChart3, AlertTriangle, Gavel, UserCheck, Link2, Shield, Building2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "../../lib/textron-data-processor";
import { FilteredAnalyticalTable } from "../planning/filtered-analytical-table";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";
import EngagementAcceptanceData from "../../constants/EngagementAcceptance.json";
import UnderstandingEntityData from "../../constants/understandingEntity.json";
import FraudRiskData from "../../constants/FraudRisk.json";

interface DetailedPlanningReviewDashboardProps {
  setActiveSection?: (section: string) => void;
}

interface MaterialityTableData {
  entity: {
    planningMateriality: number;
    revisedMateriality: number | null;
    performanceMateriality: number;
    ctt: number;
  };
  abcotds: Array<{
    name: string;
    planningMateriality: number;
    revisedMateriality: string; // Always "NA"
    performanceMateriality: number;
    ctt: string; // Always "NA"
  }>;
  components: Array<{
    name: string;
    planningMateriality: number;
    revisedMateriality: string; // Always "NA"
    performanceMateriality: number;
    ctt: number;
  }>;
}

interface ExpertData {
  name: string;
  organization: string;
  managementEstimate: string;
}

interface RelatedPartyRow {
  "Name of Party": string;
  "Nature of Relationship": string;
  "Has a transaction occurred in current period?": string;
  "Nature of transactions": string;
  "Workspace": string[] | string;
  "RoMM": string;
}

interface SignificantRisk {
  rommId: string;
  workspace: string;
  description: string;
  controlIds: string[];
  procedureIds: string[];
}

interface LitigationRow {
  subjectMatter: string;
  forum: string;
  amountInvolved: string;
  description: string;
  managementResponse: string;
  responseOfLegalCounsel: string;
}

interface ManagementEstimateRow {
  col_0: string; // Name of Estimate
  col_1: string; // ABCOTD
  col_2: string; // Estimation Uncertainty
  col_3: string; // Whether Management has used an expert
  col_4: string; // Includes fair valuation?
  col_5: string; // Whether auditor's specialist is involved?
}

interface LawsRegulationsRow {
  specific_laws_or_regulations: string;
  classification: string;
}

interface IndependenceSummary {
  totalPeople: number;
  declaredIndependence: number;
  yetToDeclare: number;
  addedNotDeclared: number;
  declaredNotAdded: number;
}

export function DetailedPlanningReviewDashboard({ setActiveSection }: DetailedPlanningReviewDashboardProps = {}) {
  const { currentEngagement } = useEngagement();
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // Section 1: Engagement Acceptance & Continuance - AE-5
  const [ae5Response, setAe5Response] = useState<any>(null);
  const [ae5Loading, setAe5Loading] = useState(true);

  // Section 2: Accounting Policies - AP-1
  const [ap1Response, setAp1Response] = useState<any>(null);
  const [ap1Loading, setAp1Loading] = useState(true);

  // Section 3: Materiality
  const [materialityTableData, setMaterialityTableData] = useState<MaterialityTableData>({
    entity: {
      planningMateriality: 0,
      revisedMateriality: null,
      performanceMateriality: 0,
      ctt: 0,
    },
    abcotds: [],
    components: [],
  });
  const [materialityLoading, setMaterialityLoading] = useState(true);

  // Section 5: Preliminary Analytical Procedures
  const [balanceSheetData, setBalanceSheetData] = useState<any[]>([]);
  const [profitLossData, setProfitLossData] = useState<any[]>([]);
  const [analyticalLoading, setAnalyticalLoading] = useState(true);
  const [materialityThresholds, setMaterialityThresholds] = useState<any>({});
  const [closingBalanceDate, setClosingBalanceDate] = useState<string>("");
  const [openingBalanceDate, setOpeningBalanceDate] = useState<string>("");

  // Section 4: Engagement Team (Independence)
  const [independenceSummary, setIndependenceSummary] = useState<IndependenceSummary>({
    totalPeople: 0,
    declaredIndependence: 0,
    yetToDeclare: 0,
    addedNotDeclared: 0,
    declaredNotAdded: 0,
  });
  const [engagementTeamLoading, setEngagementTeamLoading] = useState(true);

  // Section 6: Fraud Inquiries
  const [fra1Answer, setFra1Answer] = useState<string | null>(null);
  const [fra5Response, setFra5Response] = useState<any>(null);
  const [fra11Response, setFra11Response] = useState<any>(null);
  const [fraudLoading, setFraudLoading] = useState(true);

  // Section 7: Significant Risks
  const [significantRisks, setSignificantRisks] = useState<SignificantRisk[]>([]);
  const [risksLoading, setRisksLoading] = useState(true);

  // Section 8: Litigations & Claims
  const [litigationsTable, setLitigationsTable] = useState<LitigationRow[]>([]);
  const [litigationsLoading, setLitigationsLoading] = useState(true);

  // Section 9: Summary of Management Estimates
  const [rembEstimatesTable, setRembEstimatesTable] = useState<ManagementEstimateRow[]>([]);
  const [estimatesLoading, setEstimatesLoading] = useState(true);

  // Section 10: Management Experts
  const [expertData, setExpertData] = useState<ExpertData[]>([]);
  const [expertsLoading, setExpertsLoading] = useState(true);

  // Section 11: Related Parties
  const [relatedPartiesTable, setRelatedPartiesTable] = useState<RelatedPartyRow[]>([]);
  const [relatedPartiesLoading, setRelatedPartiesLoading] = useState(true);

  // Section 12: Summary of Laws and Regulations
  const [lawsRegulationsTable, setLawsRegulationsTable] = useState<LawsRegulationsRow[]>([]);
  const [lawsRegulationsLoading, setLawsRegulationsLoading] = useState(true);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load AE-5 (Engagement Acceptance & Continuance)
  useEffect(() => {
    const loadAE5 = async () => {
      if (!companyName) return;
      setAe5Loading(true);
      try {
        // Load directly from Planning_UserResponses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Planning_UserResponses.json");
          if (result?.success && result?.data) {
            // Planning_UserResponses.json structure:
            // { companyName: "...", sectionName: "...", responses: { "AE-5": {...} } }
            const data = result.data;
            
            // Handle both single object and array of objects
            const dataArray = Array.isArray(data) ? data : [data];
            let foundAE5 = false;
            
            for (const sectionData of dataArray) {
              // Check if this section matches Engagement Acceptance
              if (sectionData.sectionName === "Engagement Acceptance & Continuance" || 
                  sectionData.sectionKey === "engagement-acceptance") {
                
                // Check company name matches
                if (!sectionData.companyName || sectionData.companyName === companyName) {
                  const ae5Data = sectionData.responses?.["AE-5"];
                  if (ae5Data) {
                    console.log("Found AE-5 data:", ae5Data);
                    setAe5Response({
                      documents: ae5Data.documents || [],
                      textResponse: ae5Data.textResponse || null,
                    });
                    foundAE5 = true;
                    break;
                  }
                }
              }
            }
            
            // Fallback: Try using planningAPI.loadResponses
            if (!foundAE5) {
              const planningAPI = (window as any).planningAPI;
              if (planningAPI?.loadResponses) {
                const sharePointResponses = await planningAPI.loadResponses({
                  companyName,
                  sectionKey: "engagement-acceptance"
                });
                if (sharePointResponses?.success && sharePointResponses?.data) {
                  const fallbackResponses = sharePointResponses.data.responses || sharePointResponses.data;
                  const fallbackAe5 = fallbackResponses["AE-5"];
                  if (fallbackAe5) {
                    setAe5Response({
                      documents: fallbackAe5.documents || [],
                      textResponse: fallbackAe5.textResponse || null,
                    });
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load AE-5:", error);
      } finally {
        setAe5Loading(false);
      }
    };
    loadAE5();
  }, [companyName]);

  // Load AP-1 (Accounting Policies)
  useEffect(() => {
    const loadAP1 = async () => {
      if (!companyName) return;
      setAp1Loading(true);
      try {
        // Load directly from Planning_UserResponses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Planning_UserResponses.json");
          if (result?.success && result?.data) {
            // Planning_UserResponses.json structure:
            // { companyName: "...", sectionName: "...", responses: { "AP-1": {...} } }
            const data = result.data;
            
            // Handle both single object and array of objects
            const dataArray = Array.isArray(data) ? data : [data];
            let foundAP1 = false;
            
            for (const sectionData of dataArray) {
              // Check if this section matches Understanding Entity
              if (sectionData.sectionName === "Understanding the Entity and its Environment" || 
                  sectionData.sectionKey === "understanding-entity") {
                
                // Check company name matches
                if (!sectionData.companyName || sectionData.companyName === companyName) {
                  const ap1Data = sectionData.responses?.["AP-1"];
                  if (ap1Data) {
                    console.log("Found AP-1 data:", ap1Data);
                    setAp1Response({
                      documents: ap1Data.documents || [],
                      textResponse: ap1Data.textResponse || null,
                    });
                    foundAP1 = true;
                    break;
                  }
                }
              }
            }
            
            // Fallback: Try using planningAPI.loadResponses
            if (!foundAP1) {
              const planningAPI = (window as any).planningAPI;
              if (planningAPI?.loadResponses) {
                const sharePointResponses = await planningAPI.loadResponses({
                  companyName,
                  sectionKey: "understanding-entity"
                });
                if (sharePointResponses?.success && sharePointResponses?.data) {
                  const fallbackResponses = sharePointResponses.data.responses || sharePointResponses.data;
                  const fallbackAp1 = fallbackResponses["AP-1"];
                  if (fallbackAp1) {
                    setAp1Response({
                      documents: fallbackAp1.documents || [],
                      textResponse: fallbackAp1.textResponse || null,
                    });
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load AP-1:", error);
      } finally {
        setAp1Loading(false);
      }
    };
    loadAP1();
  }, [companyName]);

  // Load Independence summary for Engagement Team section
  useEffect(() => {
    const loadIndependenceSummary = async () => {
      setEngagementTeamLoading(true);
      try {
        const [responseResult, teamResult] = await Promise.allSettled([
          (window as any).sharePointAPI?.downloadClientJson?.("Independence_Response.json"),
          currentEngagement ? apiService.getEngagementTeam(currentEngagement.id) : Promise.resolve([]),
        ]);

        const responseJson =
          responseResult.status === "fulfilled" &&
          responseResult.value?.success &&
          responseResult.value?.data &&
          typeof responseResult.value.data === "object" &&
          !Array.isArray(responseResult.value.data)
            ? (responseResult.value.data as Record<string, any>)
            : {};

        const teamMembers =
          teamResult.status === "fulfilled" && Array.isArray(teamResult.value)
            ? teamResult.value
            : [];

        const byId = new Map<
          string,
          { fromResponse?: any; fromTeam: boolean; name: string }
        >();

        Object.entries(responseJson).forEach(([key, entry]) => {
          if (!entry || typeof entry !== "object") return;
          const id = String(entry?.user?.id || key);
          const name = String(entry?.user?.name || key);
          byId.set(id, { fromResponse: entry, fromTeam: false, name });
        });

        teamMembers.forEach((member: any) => {
          const id = String(member?.id ?? member?.user_id ?? "");
          const name =
            String(
              member?.user_name ??
                [member?.first_name, member?.last_name].filter(Boolean).join(" ") ??
                member?.email ??
                ""
            ).trim() || id;
          const existing = byId.get(id);
          if (existing) {
            existing.fromTeam = true;
          } else {
            const byName = Array.from(byId.entries()).find(
              ([_, v]) => v.name.trim().toLowerCase() === name.toLowerCase()
            );
            if (byName) {
              byName[1].fromTeam = true;
            } else if (id) {
              byId.set(id, { fromTeam: true, name });
            }
          }
        });

        const mergedRows = Array.from(byId.values());
        const totalPeople = mergedRows.length;
        const declaredIndependence = mergedRows.filter(
          (r) => String(r.fromResponse?.submitted_at ?? "").trim() !== ""
        ).length;
        const yetToDeclare = totalPeople - declaredIndependence;

        const addedNotDeclared = mergedRows.filter((r) => {
          const added = r.fromTeam;
          const declared = String(r.fromResponse?.submitted_at ?? "").trim() !== "";
          return added && !declared;
        }).length;

        const declaredNotAdded = mergedRows.filter((r) => {
          const added = r.fromTeam;
          const declared = String(r.fromResponse?.submitted_at ?? "").trim() !== "";
          return declared && !added;
        }).length;

        setIndependenceSummary({
          totalPeople,
          declaredIndependence,
          yetToDeclare,
          addedNotDeclared,
          declaredNotAdded,
        });
      } catch (error) {
        console.error("Failed to load engagement team independence summary:", error);
        setIndependenceSummary({
          totalPeople: 0,
          declaredIndependence: 0,
          yetToDeclare: 0,
          addedNotDeclared: 0,
          declaredNotAdded: 0,
        });
      } finally {
        setEngagementTeamLoading(false);
      }
    };
    loadIndependenceSummary();
  }, [currentEngagement?.id]);

  // Load Materiality
  useEffect(() => {
    const loadMateriality = async () => {
      if (!companyName) return;
      setMaterialityLoading(true);
      try {
        // Load directly from Planning_UserResponses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Planning_UserResponses.json");
          if (result?.success && result?.data) {
            // Planning_UserResponses.json structure:
            // { companyName: "...", sectionName: "...", responses: { "M-3": {...}, "revisedMateriality": {...}, ... } }
            const data = result.data;
            
            // Handle both single object and array of objects
            const dataArray = Array.isArray(data) ? data : [data];
            let materialityResponses: any = null;
            
            for (const sectionData of dataArray) {
              // Check if this section matches Materiality
              if (sectionData.sectionName === "Materiality" || 
                  sectionData.sectionKey === "materiality" ||
                  (sectionData.responses && (sectionData.responses["M-3"] || sectionData.responses["revisedMateriality"]))) {
                
                // Check company name matches
                if (!sectionData.companyName || sectionData.companyName === companyName) {
                  materialityResponses = sectionData.responses || {};
                  break;
                }
              }
            }
            
            // Fallback: Try using planningAPI.loadResponses
            if (!materialityResponses) {
              const planningAPI = (window as any).planningAPI;
              if (planningAPI?.loadResponses) {
                const sharePointResponses = await planningAPI.loadResponses({
                  companyName,
                  sectionKey: "materiality"
                });
                if (sharePointResponses?.success && sharePointResponses?.data) {
                  materialityResponses = sharePointResponses.data.responses || sharePointResponses.data;
                }
              }
            }
            
            if (materialityResponses) {
              // Entity column data
              // Planning Materiality: M-3.textResponse
              const m3Response = materialityResponses["M-3"];
              let planningMateriality = 0;
              if (m3Response?.textResponse) {
                const textValue = typeof m3Response.textResponse === "string"
                  ? m3Response.textResponse
                  : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
                planningMateriality = parseFloat(textValue) || 0;
              }

              // Revised Materiality: revisedMateriality.determinedMateriality
              const revisedMaterialityResponse = materialityResponses["revisedMateriality"];
              const revisedMateriality = revisedMaterialityResponse?.determinedMateriality > 0
                ? revisedMaterialityResponse.determinedMateriality
                : null;

              // Performance Materiality: revisedMateriality.performanceMateriality OR M-5.overridePerformanceMateriality
              let performanceMateriality = 0;
              if (revisedMaterialityResponse?.performanceMateriality > 0) {
                performanceMateriality = revisedMaterialityResponse.performanceMateriality;
              } else {
                const m5Response = materialityResponses["M-5"];
                if (m5Response?.overridePerformanceMateriality) {
                  performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality) || 0;
                }
              }

              // CTT: revisedMateriality.trivialThreshold OR M-8.textResponse
              let ctt = 0;
              if (revisedMaterialityResponse?.trivialThreshold > 0) {
                ctt = revisedMaterialityResponse.trivialThreshold;
              } else {
                const m8Response = materialityResponses["M-8"];
                if (m8Response?.textResponse) {
                  const textValue = typeof m8Response.textResponse === "string"
                    ? m8Response.textResponse
                    : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
                  ctt = parseFloat(textValue) || 0;
                }
              }

              // ABCOTD columns
              const abcotds: Array<{
                name: string;
                planningMateriality: number;
                revisedMateriality: string;
                performanceMateriality: number;
                ctt: string;
              }> = [];
              
              const abcotdTable = materialityResponses["abcotdTable"];
              if (abcotdTable?.columns && Array.isArray(abcotdTable.columns)) {
                abcotdTable.columns.forEach((col: any) => {
                  if (col.values) {
                    abcotds.push({
                      name: col.values.abcotd || "",
                      planningMateriality: col.values.determinedMateriality || 0,
                      revisedMateriality: "NA",
                      performanceMateriality: col.values.determinedPM || 0,
                      ctt: "NA",
                    });
                  }
                });
              }

              // Component columns
              const components: Array<{
                name: string;
                planningMateriality: number;
                revisedMateriality: string;
                performanceMateriality: number;
                ctt: number;
              }> = [];
              
              const componentsData = materialityResponses["components"];
              if (componentsData && Array.isArray(componentsData)) {
                componentsData.forEach((comp: any) => {
                  if (comp.componentName && comp.table?.rows) {
                    const materialityRow = comp.table.rows.find((r: any) => r.particular === "Materiality");
                    const performanceRow = comp.table.rows.find((r: any) => r.particular === "Performance Materiality");
                    const cttRow = comp.table.rows.find((r: any) => r.particular === "Clearly Trivial Threshold");
                    
                    components.push({
                      name: comp.componentName || "",
                      planningMateriality: materialityRow?.determinedAmount || 0,
                      revisedMateriality: "NA",
                      performanceMateriality: performanceRow?.determinedAmount || 0,
                      ctt: cttRow?.determinedAmount || 0,
                    });
                  }
                });
              }

              setMaterialityTableData({
                entity: {
                  planningMateriality,
                  revisedMateriality,
                  performanceMateriality,
                  ctt,
                },
                abcotds,
                components,
              });

              // Set materiality thresholds for FilteredAnalyticalTable
              setMaterialityThresholds({
                group_materiality: planningMateriality,
                revised_materiality: revisedMateriality && revisedMateriality > 0 ? revisedMateriality : undefined,
                performance_materiality: performanceMateriality,
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to load materiality:", error);
      } finally {
        setMaterialityLoading(false);
      }
    };
    loadMateriality();
  }, [companyName]);

  // Load Preliminary Analytical Procedures (Balance Sheet and P&L)
  useEffect(() => {
    const loadAnalyticalData = async () => {
      if (!companyName) return;
      setAnalyticalLoading(true);
      try {
        // Load from local JSON file (same as preliminary-analytics-section.tsx)
        const data = await (window as any).planning?.readSection?.(
          companyName,
          "preliminary-analytical"
        );
        
        if (data && data.analyticalData) {
          // Filter Balance Sheet items (Asset, Liability, Equity)
          const balanceSheet = data.analyticalData.filter((item: any) => 
            item.type === "Asset" || item.type === "Liability" || item.type === "Equity"
          );
          
          // Filter Profit & Loss items (Income, Expense)
          const profitLoss = data.analyticalData.filter((item: any) => 
            item.type === "Income" || item.type === "Expense"
          );
          
          setBalanceSheetData(balanceSheet);
          setProfitLossData(profitLoss);
        }

        // Also try loading from SharePoint for Balance Sheet and P&L separately
        // (same approach as preliminary-analytics-section.tsx)
        if ((window as any).sharePointAPI) {
          const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping?.({ includeAmounts: true });
          if (mappingResult?.success && mappingResult?.data) {
            const mappingData = mappingResult.data;
            
            // Load Balance Sheet data
            const parSetBS = new Set<string>();
            const parToLedgerIdsBS: Record<string, Set<number>> = {};
            const allDatesBS: string[] = [];

            for (const [bookName, entries] of Object.entries(mappingData)) {
              if (Array.isArray(entries)) {
                entries.forEach((entry: any) => {
                  const entryType = entry.type || "";
                  const entryPar = entry.par || "";
                  
                  if (entry.amount && typeof entry.amount === 'object') {
                    Object.keys(entry.amount).forEach(date => {
                      if (!allDatesBS.includes(date)) {
                        allDatesBS.push(date);
                      }
                    });
                  }
                  
                  if ((entryType === "Asset" || entryType === "Liability" || entryType === "Equity") && entryPar) {
                    parSetBS.add(entryPar);
                    if (!parToLedgerIdsBS[entryPar]) {
                      parToLedgerIdsBS[entryPar] = new Set<number>();
                    }
                    const ledgerId = entry.ledger_id || entry.ledgerId;
                    if (ledgerId) {
                      parToLedgerIdsBS[entryPar].add(ledgerId);
                    }
                  }
                });
              }
            }

            const parseDate = (dateStr: string): Date => {
              const [day, month, year] = dateStr.split('/');
              return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
            };

            const sortedDatesBS = allDatesBS.sort((a, b) => {
              const dateA = parseDate(a);
              const dateB = parseDate(b);
              return dateB.getTime() - dateA.getTime();
            });

            const latestDateBS = sortedDatesBS[0];
            if (latestDateBS) {
              const latestDateObjBS = parseDate(latestDateBS);
              const previousFYDateBS = new Date(latestDateObjBS);
              previousFYDateBS.setFullYear(previousFYDateBS.getFullYear() - 1);
              const previousFYDateStrBS = `${String(previousFYDateBS.getDate()).padStart(2, '0')}/${String(previousFYDateBS.getMonth() + 1).padStart(2, '0')}/${String(previousFYDateBS.getFullYear() % 100).padStart(2, '0')}`;
              const previousFYDateExistsBS = allDatesBS.includes(previousFYDateStrBS);
              const openingDateBS = previousFYDateExistsBS ? previousFYDateStrBS : sortedDatesBS.find(d => {
                const dObj = parseDate(d);
                return dObj.getFullYear() < latestDateObjBS.getFullYear();
              }) || "";

              setClosingBalanceDate(latestDateBS);
              setOpeningBalanceDate(openingDateBS);

              const balanceSheetItems: any[] = Array.from(parSetBS).map((par) => {
                const ledgerIds = parToLedgerIdsBS[par] || new Set<number>();
                let closingBalance = 0;
                let openingBalance = 0;

                for (const [bookName, entries] of Object.entries(mappingData)) {
                  if (Array.isArray(entries)) {
                    entries.forEach((entry: any) => {
                      const entryPar = entry.par || "";
                      const ledgerId = entry.ledger_id || entry.ledgerId;
                      if (entryPar === par && ledgerIds.has(ledgerId)) {
                        if (entry.amount && typeof entry.amount === 'object') {
                          closingBalance += parseFloat(entry.amount[latestDateBS] || "0") || 0;
                          openingBalance += parseFloat(entry.amount[openingDateBS] || "0") || 0;
                        }
                      }
                    });
                  }
                }

                const variance = closingBalance - openingBalance;
                const variancePercentage = openingBalance !== 0 ? (variance / Math.abs(openingBalance)) * 100 : 0;

                return {
                  par,
                  closing_balance: closingBalance,
                  opening_balance: openingBalance,
                  variance,
                  variance_percentage: variancePercentage,
                  type: "Asset", // Will be determined from mapping
                };
              });

              // Merge with existing data if available
              if (balanceSheetItems.length > 0) {
                setBalanceSheetData(prev => {
                  const merged = [...prev];
                  balanceSheetItems.forEach(item => {
                    const existing = merged.find(e => e.par === item.par);
                    if (existing) {
                      Object.assign(existing, item);
                    } else {
                      merged.push(item);
                    }
                  });
                  return merged;
                });
              }
            }

            // Load Profit & Loss data (similar logic)
            const parSetPL = new Set<string>();
            const parToLedgerIdsPL: Record<string, Set<number>> = {};
            const allDatesPL: string[] = [];

            for (const [bookName, entries] of Object.entries(mappingData)) {
              if (Array.isArray(entries)) {
                entries.forEach((entry: any) => {
                  const entryType = entry.type || "";
                  const entryPar = entry.par || "";
                  
                  if (entry.amount && typeof entry.amount === 'object') {
                    Object.keys(entry.amount).forEach(date => {
                      if (!allDatesPL.includes(date)) {
                        allDatesPL.push(date);
                      }
                    });
                  }
                  
                  if ((entryType === "Income" || entryType === "Expense") && entryPar) {
                    parSetPL.add(entryPar);
                    if (!parToLedgerIdsPL[entryPar]) {
                      parToLedgerIdsPL[entryPar] = new Set<number>();
                    }
                    const ledgerId = entry.ledger_id || entry.ledgerId;
                    if (ledgerId) {
                      parToLedgerIdsPL[entryPar].add(ledgerId);
                    }
                  }
                });
              }
            }

            const sortedDatesPL = allDatesPL.sort((a, b) => {
              const dateA = parseDate(a);
              const dateB = parseDate(b);
              return dateB.getTime() - dateA.getTime();
            });

            const latestDatePL = sortedDatesPL[0];
            if (latestDatePL) {
              const latestDateObjPL = parseDate(latestDatePL);
              const previousFYDatePL = new Date(latestDateObjPL);
              previousFYDatePL.setFullYear(previousFYDatePL.getFullYear() - 1);
              const previousFYDateStrPL = `${String(previousFYDatePL.getDate()).padStart(2, '0')}/${String(previousFYDatePL.getMonth() + 1).padStart(2, '0')}/${String(previousFYDatePL.getFullYear() % 100).padStart(2, '0')}`;
              const previousFYDateExistsPL = allDatesPL.includes(previousFYDateStrPL);
              const openingDatePL = previousFYDateExistsPL ? previousFYDateStrPL : sortedDatesPL.find(d => {
                const dObj = parseDate(d);
                return dObj.getFullYear() < latestDateObjPL.getFullYear();
              }) || "";

              // Use PL dates if BS dates weren't set
              if (!closingBalanceDate) {
                setClosingBalanceDate(latestDatePL);
                setOpeningBalanceDate(openingDatePL);
              }

              const profitLossItems: any[] = Array.from(parSetPL).map((par) => {
                const ledgerIds = parToLedgerIdsPL[par] || new Set<number>();
                let closingBalance = 0;
                let openingBalance = 0;

                for (const [bookName, entries] of Object.entries(mappingData)) {
                  if (Array.isArray(entries)) {
                    entries.forEach((entry: any) => {
                      const entryPar = entry.par || "";
                      const ledgerId = entry.ledger_id || entry.ledgerId;
                      if (entryPar === par && ledgerIds.has(ledgerId)) {
                        if (entry.amount && typeof entry.amount === 'object') {
                          closingBalance += parseFloat(entry.amount[latestDatePL] || "0") || 0;
                          openingBalance += parseFloat(entry.amount[openingDatePL] || "0") || 0;
                        }
                      }
                    });
                  }
                }

                const variance = closingBalance - openingBalance;
                const variancePercentage = openingBalance !== 0 ? (variance / Math.abs(openingBalance)) * 100 : 0;

                return {
                  par,
                  closing_balance: closingBalance,
                  opening_balance: openingBalance,
                  variance,
                  variance_percentage: variancePercentage,
                  type: "Income", // Will be determined from mapping
                };
              });

              if (profitLossItems.length > 0) {
                setProfitLossData(prev => {
                  const merged = [...prev];
                  profitLossItems.forEach(item => {
                    const existing = merged.find(e => e.par === item.par);
                    if (existing) {
                      Object.assign(existing, item);
                    } else {
                      merged.push(item);
                    }
                  });
                  return merged;
                });
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load analytical data:", error);
      } finally {
        setAnalyticalLoading(false);
      }
    };
    loadAnalyticalData();
  }, [companyName]);

  // Load Fraud Inquiries (FRA-1, FRA-5, FRA-11)
  useEffect(() => {
    const loadFraudData = async () => {
      if (!companyName) return;
      setFraudLoading(true);
      try {
        // Load directly from Planning_UserResponses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Planning_UserResponses.json");
          if (result?.success && result?.data) {
            // Planning_UserResponses.json structure:
            // { companyName: "...", sectionName: "...", responses: { "FRA-1": {...}, "FRA-5": {...}, "FRA-11": {...} } }
            const data = result.data;
            
            // Handle both single object and array of objects
            const dataArray = Array.isArray(data) ? data : [data];
            let foundFraudData = false;
            
            for (const sectionData of dataArray) {
              // Check if this section matches Fraud Risk Assessment
              if (sectionData.sectionName === "Fraud Risk Assessment & Response" || 
                  sectionData.sectionKey === "fraud-risk-assessment") {
                
                // Check company name matches
                if (!sectionData.companyName || sectionData.companyName === companyName) {
                  const responses = sectionData.responses || {};
                  
                  // Load FRA-1 answer
                  const fra1Response = responses["FRA-1"];
                  const fra1AnswerValue = fra1Response?.selectedOption || null;
                  setFra1Answer(fra1AnswerValue);

                  // Load FRA-5 if FRA-1 is "Yes", otherwise load FRA-11
                  if (fra1AnswerValue === "Yes") {
                    const fra5Data = responses["FRA-5"];
                    if (fra5Data) {
                      setFra5Response({
                        relatedPartiesTable: fra5Data.relatedPartiesTable || fra5Data.table?.rows || [],
                        table: fra5Data.table || null,
                      });
                    }
                    setFra11Response(null);
                  } else if (fra1AnswerValue === "No") {
                    const fra11Data = responses["FRA-11"];
                    if (fra11Data) {
                      setFra11Response({
                        textResponse: fra11Data.textResponse || null,
                      });
                    }
                    setFra5Response(null);
                  } else {
                    setFra5Response(null);
                    setFra11Response(null);
                  }
                  foundFraudData = true;
                  break;
                }
              }
            }
            
            // Fallback: Try using planningAPI.loadResponses
            if (!foundFraudData) {
              const planningAPI = (window as any).planningAPI;
              if (planningAPI?.loadResponses) {
                const sharePointResponses = await planningAPI.loadResponses({
                  companyName,
                  sectionKey: "fraud-risk-assessment"
                });
                if (sharePointResponses?.success && sharePointResponses?.data) {
                  const fallbackResponses = sharePointResponses.data.responses || sharePointResponses.data;
                  
                  const fallbackFra1 = fallbackResponses["FRA-1"];
                  const fallbackFra1Answer = fallbackFra1?.selectedOption || null;
                  setFra1Answer(fallbackFra1Answer);
                  
                  if (fallbackFra1Answer === "Yes") {
                    const fallbackFra5 = fallbackResponses["FRA-5"];
                    if (fallbackFra5) {
                      setFra5Response({
                        relatedPartiesTable: fallbackFra5.relatedPartiesTable || fallbackFra5.table?.rows || [],
                        table: fallbackFra5.table || null,
                      });
                    }
                    setFra11Response(null);
                  } else if (fallbackFra1Answer === "No") {
                    const fallbackFra11 = fallbackResponses["FRA-11"];
                    if (fallbackFra11) {
                      setFra11Response({
                        textResponse: fallbackFra11.textResponse || null,
                      });
                    }
                    setFra5Response(null);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load fraud data:", error);
      } finally {
        setFraudLoading(false);
      }
    };
    loadFraudData();
  }, [companyName]);

  // Load Significant Risks
  useEffect(() => {
    const loadSignificantRisks = async () => {
      setRisksLoading(true);
      try {
        if ((window as any).sharePointAPI?.readRommLibrary) {
          const rommRes = await (window as any).sharePointAPI.readRommLibrary();
          if (rommRes.success && rommRes.data?.romm_library) {
            const romms = Array.isArray(rommRes.data.romm_library) ? rommRes.data.romm_library : [];
            const significant = romms.filter((romm: any) => {
              const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
              return assessment.includes("significant");
            });

            const risks: SignificantRisk[] = significant.map((romm: any) => {
              const rommId = romm["romm-id"] || romm.romm_id || "";
              const workspace = Array.isArray(romm.workspace)
                ? romm.workspace[0] || ""
                : romm.workspace || "";
              const description = romm.description || "";
              const controlIds = Array.isArray(romm.control_id)
                ? romm.control_id.map(String)
                : romm.control_id
                ? [String(romm.control_id)]
                : [];
              const procedureIds = Array.isArray(romm.procedure_id)
                ? romm.procedure_id.map(String)
                : romm.procedure_id
                ? [String(romm.procedure_id)]
                : [];

              return {
                rommId,
                workspace,
                description,
                controlIds,
                procedureIds,
              };
            });

            setSignificantRisks(risks);
          }
        }
      } catch (error) {
        console.error("Error loading significant risks:", error);
      } finally {
        setRisksLoading(false);
      }
    };
    loadSignificantRisks();
  }, []);

  // Load Litigations & Claims (same as partner review dashboard)
  useEffect(() => {
    const loadLitigations = async () => {
      setLitigationsLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_LIT_Substantive.json");
          if (result?.success && result.data) {
            const data = result.data;
            // The litigations table is stored in substantiveProcedures.litigations
            const substantiveProcedures = data.substantiveProcedures || {};
            const litigations = substantiveProcedures.litigations;
            if (Array.isArray(litigations)) {
              setLitigationsTable(litigations);
            } else {
              setLitigationsTable([]);
            }
          }
        }
      } catch (error) {
        console.error("Error loading litigations table:", error);
      } finally {
        setLitigationsLoading(false);
      }
    };
    loadLitigations();
  }, []);

  // Load Summary of Management Estimates
  useEffect(() => {
    const loadManagementEstimates = async () => {
      setEstimatesLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          if (result?.success && result?.data) {
            const workspaceName = "Review of estimates for management bias";
            const remb1Data = result.data[workspaceName]?.["REMB-1"];
            if (remb1Data?.table && Array.isArray(remb1Data.table)) {
              setRembEstimatesTable(remb1Data.table);
            }
          }
        }
      } catch (error) {
        console.error("Error loading management estimates:", error);
      } finally {
        setEstimatesLoading(false);
      }
    };
    loadManagementEstimates();
  }, []);

  // Load Management Experts
  useEffect(() => {
    const loadManagementExperts = async () => {
      setExpertsLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Execution_TailoringQuestions_Responses.json");
          if (result?.success && result?.data) {
            const areaData = result.data["Management Use of Expert"];
            if (areaData) {
              const savedData = areaData["mue-expert-details"];
              if (savedData?.table) {
                const savedTable = savedData.table;
                if (savedTable.columns && savedTable.rows && savedTable.rows.length >= 3) {
                  const expertCols = savedTable.columns.slice(1); // Skip "Particulars" column
                  const experts: ExpertData[] = expertCols.map((colName: string, colIndex: number) => {
                    const nameRow = savedTable.rows[0] || {};
                    const orgRow = savedTable.rows[1] || {};
                    const estimateRow = savedTable.rows[2] || {};
                    
                    return {
                      name: nameRow[colName] || "",
                      organization: orgRow[colName] || "",
                      managementEstimate: estimateRow[colName] || "",
                    };
                  });
                  setExpertData(experts);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading management experts:", error);
      } finally {
        setExpertsLoading(false);
      }
    };
    loadManagementExperts();
  }, []);

  // Load Related Parties
  useEffect(() => {
    const loadRelatedParties = async () => {
      setRelatedPartiesLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Execution_TailoringQuestions_Responses.json");
          if (result?.success && result?.data) {
            const areaData = result.data["Related Party Disclosure"];
            if (areaData) {
              const savedData = areaData["related-party-list"];
              if (savedData?.table) {
                const savedTable = savedData.table;
                if (savedTable.columns && savedTable.rows && savedTable.rows.length > 0) {
                  const loadedRows: RelatedPartyRow[] = savedTable.rows.map((row: any) => ({
                    "Name of Party": row["Name of Party"] || "",
                    "Nature of Relationship": row["Nature of Relationship"] || "",
                    "Has a transaction occurred in current period?": row["Has a transaction occurred in current period?"] || "",
                    "Nature of transactions": row["Nature of transactions"] || "",
                    "Workspace": Array.isArray(row["Workspace"]) ? row["Workspace"] : (row["Workspace"] ? [row["Workspace"]] : []),
                    "RoMM": row["RoMM"] || "",
                  }));
                  setRelatedPartiesTable(loadedRows);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading related parties:", error);
      } finally {
        setRelatedPartiesLoading(false);
      }
    };
    loadRelatedParties();
  }, []);

  // Load Summary of Laws and Regulations (first table)
  useEffect(() => {
    const loadLawsRegulations = async () => {
      setLawsRegulationsLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          if (result?.success && result?.data) {
            const workspaceName = "Compliance with laws and regulations";
            const clr1Data = result.data[workspaceName]?.["CLR-1"];
            if (clr1Data?.table && Array.isArray(clr1Data.table)) {
              const rows: LawsRegulationsRow[] = clr1Data.table.map((row: any) => ({
                specific_laws_or_regulations: row.specific_laws_or_regulations || "",
                classification: row.classification || "",
              }));
              setLawsRegulationsTable(rows);
            }
          }
        }
      } catch (error) {
        console.error("Error loading laws and regulations:", error);
      } finally {
        setLawsRegulationsLoading(false);
      }
    };
    loadLawsRegulations();
  }, []);

  // Set overall loading state
  useEffect(() => {
    const allLoading = ae5Loading || ap1Loading || materialityLoading || analyticalLoading || 
                       fraudLoading || risksLoading || litigationsLoading || estimatesLoading || 
                       expertsLoading || relatedPartiesLoading || lawsRegulationsLoading;
    setLoading(allLoading);
  }, [ae5Loading, ap1Loading, materialityLoading, analyticalLoading, fraudLoading, 
       risksLoading, litigationsLoading, estimatesLoading, expertsLoading, 
       relatedPartiesLoading, lawsRegulationsLoading]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Helper function to get question text from JSON files
  const getQuestionText = (questionId: string, section: "engagement-acceptance" | "understanding-entity" | "fraud-risk") => {
    try {
      if (section === "engagement-acceptance") {
        const sections = EngagementAcceptanceData["Engagement Acceptance & Continuance"];
        for (const sectionKey in sections) {
          const questions = sections[sectionKey as keyof typeof sections];
          if (Array.isArray(questions)) {
            const question = questions.find((q: any) => q.id === questionId);
            if (question) return question.question;
          }
        }
      } else if (section === "understanding-entity") {
        const sections = UnderstandingEntityData["Understanding the Entity and its Environment"];
        for (const sectionKey in sections) {
          const questions = sections[sectionKey as keyof typeof sections];
          if (Array.isArray(questions)) {
            const question = questions.find((q: any) => q.id === questionId);
            if (question) return question.question;
          }
        }
      } else if (section === "fraud-risk") {
        const sections = FraudRiskData["Fraud Risk Assessment & Response"];
        for (const sectionKey in sections) {
          const sectionData = sections[sectionKey as keyof typeof sections];
          if (Array.isArray(sectionData)) {
            for (const item of sectionData) {
              if (item.id === questionId) {
                return item.question;
              }
              // Check nested questions
              if (item.questions && Array.isArray(item.questions)) {
                const question = item.questions.find((q: any) => q.id === questionId);
                if (question) return question.question;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error(`Error getting question text for ${questionId}:`, error);
    }
    return null;
  };

  if (loading && !companyName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          <span className="text-white">Loading detailed planning review dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Detailed Planning Review Dashboard</h1>
            <p className="text-white/60 mt-1">Comprehensive planning phase review</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setActiveSection?.("partner-review-planning")}
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Planning Review
          </Button>
        </div>

        {/* 1. Engagement Acceptance & Continuance - AE-5 */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Engagement Acceptance & Continuance</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Question AE-5: Agreed terms of audit engagement</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ae5Loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h4 className="text-sm font-semibold text-white/90 mb-2">Question AE-5:</h4>
                  <p className="text-sm text-white/80 whitespace-pre-line">
                    {getQuestionText("AE-5", "engagement-acceptance") || "Record the agreed terms of the audit engagement..."}
                  </p>
                </div>
                {ae5Response ? (
                  <DocumentManager
                    sectionKey="engagement-acceptance"
                    itemId="AE-5"
                    itemTextResponse={ae5Response.textResponse || null}
                    itemDocuments={(ae5Response.documents || []).map((doc: string | any) => {
                      // Handle both string and object formats
                      if (typeof doc === 'string') {
                        return {
                          id: `doc_${doc}`,
                          type: 'linked' as const,
                          name: doc
                        };
                      }
                      return doc;
                    })}
                    disabled={true}
                  />
                ) : (
                  <div className="text-white/40 text-center py-4">No response available</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Accounting Policies - AP-1 */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500/30 to-green-600/20 flex items-center justify-center">
                <Scale className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Accounting Policies</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Question AP-1: Significant accounting policies</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ap1Loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h4 className="text-sm font-semibold text-white/90 mb-2">Question AP-1:</h4>
                  <p className="text-sm text-white/80 whitespace-pre-line">
                    {getQuestionText("AP-1", "understanding-entity") || "Provide a description of the key accounting policies..."}
                  </p>
                </div>
                {ap1Response ? (
                  <DocumentManager
                    sectionKey="understanding-entity"
                    itemId="AP-1"
                    itemTextResponse={ap1Response.textResponse || null}
                    itemDocuments={(ap1Response.documents || []).map((doc: string | any) => {
                      // Handle both string and object formats
                      if (typeof doc === 'string') {
                        return {
                          id: `doc_${doc}`,
                          type: 'linked' as const,
                          name: doc
                        };
                      }
                      return doc;
                    })}
                    disabled={true}
                  />
                ) : (
                  <div className="text-white/40 text-center py-4">No response available</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Materiality */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Materiality</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Planning and performance materiality summary</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {materialityLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold w-48">Materiality Type</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-40">Entity</TableHead>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableHead key={`abcotd-${idx}`} className="text-white/80 font-semibold text-center w-40">
                          <div className="leading-tight">
                            <div className="text-xs italic text-white/60">(ABCOTD)</div>
                            <div>{abcotd.name || `ABCOTD ${idx + 1}`}</div>
                          </div>
                        </TableHead>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableHead key={`component-${idx}`} className="text-white/80 font-semibold text-center w-40">
                          <div className="leading-tight">
                            <div className="text-xs italic text-white/60">(Component)</div>
                            <div>{comp.name || `Component ${idx + 1}`}</div>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Planning Materiality Row */}
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">Planning Materiality</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {formatCurrency(materialityTableData.entity.planningMateriality)}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-pm-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(abcotd.planningMateriality)}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-pm-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(comp.planningMateriality)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {/* Revised Materiality Row */}
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">Revised Materiality</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {materialityTableData.entity.revisedMateriality !== null 
                          ? formatCurrency(materialityTableData.entity.revisedMateriality)
                          : "-"}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-rm-${idx}`} className="text-white/90 text-center">
                          {abcotd.revisedMateriality}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-rm-${idx}`} className="text-white/90 text-center">
                          {comp.revisedMateriality}
                        </TableCell>
                      ))}
                    </TableRow>
                    {/* Performance Materiality Row */}
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">Performance Materiality</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {formatCurrency(materialityTableData.entity.performanceMateriality)}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-perf-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(abcotd.performanceMateriality)}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-perf-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(comp.performanceMateriality)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {/* CTT Row */}
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">CTT</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {formatCurrency(materialityTableData.entity.ctt)}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-ctt-${idx}`} className="text-white/90 text-center">
                          {abcotd.ctt}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-ctt-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(comp.ctt)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Engagement Team */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500/30 to-indigo-600/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Engagement Team</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">
                  Independence workspace summary (Planning phase)
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {engagementTeamLoading ? (
              <div className="flex items-center justify-center h-24">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.totalPeople}</p>
                  <p className="text-xs text-white/60">Total people</p>
                </div>
                <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.declaredIndependence}</p>
                  <p className="text-xs text-white/60">Declared independence</p>
                </div>
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.yetToDeclare}</p>
                  <p className="text-xs text-white/60">Yet to declare</p>
                </div>
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.addedNotDeclared}</p>
                  <p className="text-xs text-white/60">Added but not declared</p>
                </div>
                <div className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.declaredNotAdded}</p>
                  <p className="text-xs text-white/60">Declared but not added</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5. Preliminary Analytical Procedures */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-cyan-500/30 to-cyan-600/20 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Preliminary Analytical Procedures</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Balance Sheet and Profit & Loss analysis</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {analyticalLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="space-y-6">
                <FilteredAnalyticalTable
                  title="Balance Sheet Items"
                  data={balanceSheetData}
                  materiality={materialityThresholds}
                  onChange={() => {}}
                  readonly={true}
                  filterTypes={["Asset", "Liability", "Equity"]}
                  excludeTypes={["Income", "Expense"]}
                  icon={<Building2 className="h-5 w-5" />}
                  closingBalanceDate={closingBalanceDate}
                  openingBalanceDate={openingBalanceDate}
                  showAbcotd={true}
                  showSaveButton={false}
                  areResponsesLoaded={true}
                />
                <FilteredAnalyticalTable
                  title="Profit & Loss Items"
                  data={profitLossData}
                  materiality={materialityThresholds}
                  onChange={() => {}}
                  readonly={true}
                  filterTypes={["Income", "Expense"]}
                  excludeTypes={["Asset", "Liability", "Equity"]}
                  icon={<BarChart3 className="h-5 w-5" />}
                  closingBalanceDate={closingBalanceDate}
                  openingBalanceDate={openingBalanceDate}
                  showAbcotd={false}
                  showSaveButton={false}
                  areResponsesLoaded={true}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* 6. Fraud Inquiries */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Fraud Inquiries</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">
                  {fra1Answer === "Yes" ? "Question FRA-5: Inquiries of management" : "Question FRA-11: Understanding of management's communication"}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {fraudLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : fra1Answer === "Yes" && fra5Response ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h4 className="text-sm font-semibold text-white/90 mb-2">Question FRA-5:</h4>
                  <p className="text-sm text-white/80 whitespace-pre-line">
                    {getQuestionText("FRA-5", "fraud-risk") || "Document our inquiries of the management..."}
                  </p>
                </div>
                {fra5Response.relatedPartiesTable && Array.isArray(fra5Response.relatedPartiesTable) && fra5Response.relatedPartiesTable.length > 0 ? (
                  <div className="overflow-x-auto rounded-md border border-white/10">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          <TableHead className="text-white/80 font-semibold">Role</TableHead>
                          <TableHead className="text-white/80 font-semibold">Name</TableHead>
                          <TableHead className="text-white/80 font-semibold">Position</TableHead>
                          <TableHead className="text-white/80 font-semibold">Date of Discussion</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fra5Response.relatedPartiesTable.map((row: any, idx: number) => (
                          <TableRow key={idx} className="border-white/5">
                            <TableCell className="text-white/90">{row.role || "-"}</TableCell>
                            <TableCell className="text-white/90">{row.name || "-"}</TableCell>
                            <TableCell className="text-white/90">{row.position || "-"}</TableCell>
                            <TableCell className="text-white/90">{row.date || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : fra5Response.table ? (
                  <div className="overflow-x-auto rounded-md border border-white/10">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          {fra5Response.table.columns?.map((col: string | { key: string; label: string }, idx: number) => {
                            const colLabel = typeof col === "string" ? col : col.label;
                            return (
                              <TableHead key={idx} className="text-white/80 font-semibold">{colLabel}</TableHead>
                            );
                          })}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fra5Response.table.rows?.map((row: any, idx: number) => (
                          <TableRow key={idx} className="border-white/5">
                            {fra5Response.table.columns?.map((col: string | { key: string; label: string }, colIdx: number) => {
                              const colKey = typeof col === "string" ? col : col.key;
                              return (
                                <TableCell key={colIdx} className="text-white/90">{row[colKey] || "-"}</TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-white/40 text-center py-8">No table data available</div>
                )}
              </div>
            ) : fra1Answer === "No" && fra11Response ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h4 className="text-sm font-semibold text-white/90 mb-2">Question FRA-11:</h4>
                  <p className="text-sm text-white/80 whitespace-pre-line">
                    {getQuestionText("FRA-11", "fraud-risk") || "Document our understanding of management's communication..."}
                  </p>
                </div>
                {fra11Response.textResponse ? (
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div 
                      className="text-white/90 prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: typeof fra11Response.textResponse === "string" 
                          ? fra11Response.textResponse 
                          : fra11Response.textResponse.html || fra11Response.textResponse.plainText || ""
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-white/40 text-center py-8">No response available</div>
                )}
              </div>
            ) : (
              <div className="text-white/40 text-center py-8">No response available</div>
            )}
          </CardContent>
        </Card>

        {/* 7. Significant Risks Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Significant Risks Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Risks assessed as significant</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {risksLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : significantRisks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Risk ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Control(s)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Substantive Procedures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {significantRisks.map((risk) => (
                      <tr
                        key={risk.rommId}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="py-3 px-4 text-white font-medium">{risk.rommId}</td>
                        <td className="py-3 px-4 text-white/80">{risk.workspace || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">{risk.description || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.controlIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.controlIds.map((controlId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {controlId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.procedureIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.procedureIds.map((procedureId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {procedureId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No significant risks found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 8. Litigations & Claims Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <Gavel className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Litigations & Claims Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Litigations from Assessment</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {litigationsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : litigationsTable.length > 0 ? (
              <div className="space-y-3">
                <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableHead className="text-white/80 font-medium">Subject Matter</TableHead>
                        <TableHead className="text-white/80 font-medium">Forum</TableHead>
                        <TableHead className="text-white/80 font-medium">Amount Involved</TableHead>
                        <TableHead className="text-white/80 font-medium">Description</TableHead>
                        <TableHead className="text-white/80 font-medium">Management Response</TableHead>
                        <TableHead className="text-white/80 font-medium">Response of Legal Counsel</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {litigationsTable.map((row, index) => (
                        <TableRow key={index} className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 py-3 px-4">{row.subjectMatter || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.forum || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.amountInvolved || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                            <div className="line-clamp-2">{row.description || "-"}</div>
                          </TableCell>
                          <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                            <div className="line-clamp-2">{row.managementResponse || "-"}</div>
                          </TableCell>
                          <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                            <div className="line-clamp-2">{row.responseOfLegalCounsel || "-"}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {setActiveSection && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActiveSection("concluding-litigations-claims")}
                      className="text-white/80 hover:text-white border-white/10"
                    >
                      View Details
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No litigations data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 9. Summary of Management Estimates */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Summary of Management Estimates</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Estimates from Review of Estimates for Management Bias</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {estimatesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : rembEstimatesTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Name of Estimate</TableHead>
                      <TableHead className="text-white/80 font-semibold">ABCOTD</TableHead>
                      <TableHead className="text-white/80 font-semibold">Estimation Uncertainty</TableHead>
                      <TableHead className="text-white/80 font-semibold">Whether Management has used an expert</TableHead>
                      <TableHead className="text-white/80 font-semibold">Includes fair valuation?</TableHead>
                      <TableHead className="text-white/80 font-semibold">Whether auditor's specialist is involved?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rembEstimatesTable.map((row, index) => (
                      <TableRow key={index} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white/90">{row.col_0 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_1 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_2 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_3 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_4 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_5 || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-white/40 text-center py-8">No estimates data available</div>
            )}
          </CardContent>
        </Card>

        {/* 10. Summary of Management Experts */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500/30 to-indigo-600/20 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Summary of Management Experts</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Experts used by management</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {expertsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : expertData.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Name</TableHead>
                      <TableHead className="text-white/80 font-semibold">Organization</TableHead>
                      <TableHead className="text-white/80 font-semibold">Management Estimate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expertData.map((expert, index) => (
                      <TableRow key={index} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white/90">{expert.name || "-"}</TableCell>
                        <TableCell className="text-white/90">{expert.organization || "-"}</TableCell>
                        <TableCell className="text-white/90">{expert.managementEstimate || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-white/40 text-center py-8">No experts data available</div>
            )}
          </CardContent>
        </Card>

        {/* 11. Related Parties */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500/30 to-teal-600/20 flex items-center justify-center">
                <Link2 className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Related Parties</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Related party transactions</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {relatedPartiesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : relatedPartiesTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Name of Party</TableHead>
                      <TableHead className="text-white/80 font-semibold">Nature of Relationship</TableHead>
                      <TableHead className="text-white/80 font-semibold">Has a transaction occurred in current period?</TableHead>
                      <TableHead className="text-white/80 font-semibold">Nature of transactions</TableHead>
                      <TableHead className="text-white/80 font-semibold">Workspace</TableHead>
                      <TableHead className="text-white/80 font-semibold">RoMM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relatedPartiesTable.map((row, index) => (
                      <TableRow key={index} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white/90">{row["Name of Party"] || "-"}</TableCell>
                        <TableCell className="text-white/90">{row["Nature of Relationship"] || "-"}</TableCell>
                        <TableCell className="text-white/90">{row["Has a transaction occurred in current period?"] || "-"}</TableCell>
                        <TableCell className="text-white/90">{row["Nature of transactions"] || "-"}</TableCell>
                        <TableCell className="text-white/90">
                          {Array.isArray(row["Workspace"]) ? row["Workspace"].join(", ") : row["Workspace"] || "-"}
                        </TableCell>
                        <TableCell className="text-white/90">{row["RoMM"] || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-white/40 text-center py-8">No related parties data available</div>
            )}
          </CardContent>
        </Card>

        {/* 12. Summary of Laws and Regulations */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-500/30 to-amber-600/20 flex items-center justify-center">
                <Shield className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Summary of Laws and Regulations</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Laws and regulations relevant to the entity</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {lawsRegulationsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : lawsRegulationsTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Specific laws or regulations</TableHead>
                      <TableHead className="text-white/80 font-semibold">Classification</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lawsRegulationsTable.map((row, index) => (
                      <TableRow key={index} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white/90">{row.specific_laws_or_regulations || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.classification || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-white/40 text-center py-8">No laws and regulations data available</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
